/**
 * Aircraft types for purposes of identifying the type of aircraft noise abatement applies to
 */
export enum AppliesTo {
  AllAircraft,
  CategoryA,
  CategoryB,
  CategoryC,
  CategoryD,
  CategoryE,
  JetTurbine,
  Rotorcraft,
  SmallProp
}

/**
 * Approach/Departure types for determining the phase of flight noise abatement applies to
 */
export enum AppDep {
  Approach,
  Both,
  Departure
}

/**
 * Noise abatement procedure for airport
 */
export class NoiseAbatement {
  /**
   * Unique identifier for this record in the database
   */
  id: number;

  /**
   * Airport record ID for which this record applies
   */
  airportId: number;

  /**
   * Source ID that identifies the source of this data (e.g., airport manager)
   */
  sourceId: number;

  /**
   * Date this record was created
   */
  createDate: Date;

  /**
   * Indicates the type of aircraft this noise abatement procedure applies to. This will generally either be the string
   * "All aircraft" or "See Text".
   */
  appliesTo: AppliesTo;

  /**
   * Runway this noise abatement procedure applies to. This will either be the name of the runway (e.g., "30"), the
   * string "all" if this noise abatement procedure applies to all runways, or "See Text".
   */
  runway: string;

  /**
   * Indicates whether this noise abatement procedure is relevant for approach, departure, or both (or in case of
   * special instructions, this will be the string "See Text").
   */
  appDep: AppDep;

  /**
   * Noise abatement procedure text (e.g., "clmb straight ahead to 1000' at quarry bfr turning")
   */
  text: string;

  /**
   * Last update date for this record
   */
  updateDate: Date;

  /**
   * User ID who last modified this record
   */
  updateBy: string;

  /**
   * Parses noise abatement data
   * @param data {any} Noise abatement data as returned by API
   * @return {NoiseAbatement} Parsed nosie abatement data
   */
  static parse(data: any): NoiseAbatement {
    return {
      id: data.noiseId,
      airportId: data.airportId,
      sourceId: data.sourceId,
      createDate: data.createDate ? new Date(data.createDate) : null,
      appliesTo: this.parseAppliesTo(data.appliesTo),
      runway: data.runway,
      appDep: this.parseAppDep(data.appDep),
      text: data.text,
      updateDate: data.updateDate ? new Date(data.updateDate) : null,
      updateBy: data.updateBy
    };
  }

  /**
   * Parses AppliesTo from string
   * @param value {string} AppliesTo as returned by API
   * @return {AppliesTo} Parsed AppliesTo
   */
  static parseAppliesTo(value: string): AppliesTo {
    switch (value) {
      case 'All Aircraft':
        return AppliesTo.AllAircraft;
      case 'Category A':
        return AppliesTo.CategoryA;
      case 'Category B':
        return AppliesTo.CategoryB;
      case 'Category C':
        return AppliesTo.CategoryC;
      case 'Category D':
        return AppliesTo.CategoryD;
      case 'Category E':
        return AppliesTo.CategoryE;
      case 'Small Prop':
        return AppliesTo.SmallProp;
      case 'Jet/Turbine':
        return AppliesTo.JetTurbine;
      case 'Rotorcraft':
        return AppliesTo.Rotorcraft;
      default:
        return AppliesTo.AllAircraft;
    }
  }

  /**
   * Formats AppliesTo for display
   * @param value {AppliesTo} AppliesTo to be formatted as string
   * @return {string} Formatted AppliesTo
   */
  static formatAppliesTo(value: AppliesTo): string {
    switch (value) {
      case AppliesTo.AllAircraft:
        return 'All Aircraft';
      case AppliesTo.CategoryA:
        return 'Category A';
      case AppliesTo.CategoryB:
        return 'Category B';
      case AppliesTo.CategoryC:
        return 'Category C';
      case AppliesTo.CategoryD:
        return 'Category D';
      case AppliesTo.CategoryE:
        return 'Category E';
      case AppliesTo.SmallProp:
        return 'Small Prop';
      case AppliesTo.JetTurbine:
        return 'Jet/Turbine';
      case AppliesTo.Rotorcraft:
        return 'Rotorcraft';
      default:
        return 'All Aircraft';
    }
  }

  /**
   * Parses AppDep from string
   * @param value {string} AppDep as returned by API
   * @return {AppDep} Parsed AppDep
   */
  static parseAppDep(value: string): AppDep {
    switch (value) {
      case 'Approach':
        return AppDep.Approach;
      case 'Both':
        return AppDep.Both;
      case 'Departure':
        return AppDep.Departure;
      default:
        return AppDep.Both;
    }
  }

  /**
   * Formats AppDep for display
   * @param value {AppDep} AppDep to be formatted as string
   * @return {string} Formatted AppDep
   */
  static formatAppDep(value: AppDep): string {
    switch (value) {
      case AppDep.Approach:
        return 'Approach';
      case AppDep.Both:
        return 'Both';
      case AppDep.Departure:
        return 'Departure';
      default:
        return 'Both';
    }
  }
}
